// Window-function top-level eager ops:
//
//   `(forward-fill ds :col? ... :order-by :ts :partition-by [:k] :limit N)`
//   `(backward-fill ds :col? ... :order-by :ts :partition-by [:k] :limit N)`
//   `(lag ds :col :order-by :ts :partition-by [:k] :offset 1 :as :prev)`
//   `(lead ds :col :order-by :ts :partition-by [:k] :offset 1 :as :next)`
//   `(first-value ds :col :order-by :ts :partition-by [:k] :as :first)`
//   `(last-value ds :col :order-by :ts :partition-by [:k] :as :last)`
//
// Auto-dispatch on target col kind: if the listed col(s) are
// `nested-dataset` (TS-ref) the call runs per-cell on each inner
// `{timestamp, value, ...}` dataset via `liftPerCellOnColumns`;
// otherwise it runs on the outer dataset. Mixing nested-dataset and
// plain columns in one call is a `ShapeError`.
//
// Schemas + doc text are concrete per op (their keyword fields and
// prose legitimately differ); resolution + per-cell dispatch live in
// the shared substrate at the top of this file.

import { z } from 'zod';

import type { EwmaDecay, WindowExtent } from '../../../stats/series-window.ts';
import { parseDurationMs, parseTimeUnitMs } from '../../../utils/date.ts';
import type { SourcePos } from '../../ast.ts';
import type { EvalResult } from '../../env.ts';
import { ShapeError, TypeMismatchError } from '../../errors.ts';
import { type AstInput, type OpCall, opCallPos } from '../../normalize.ts';
import { opMeta, type PropagateSchemaCtx, type SpecialCtx, type TerseGroupId } from '../../op-meta.ts';
import type { Opcode } from '../../op-surface.ts';
import { paramPath } from '../../path.ts';
import type { PlanNode } from '../../plan.ts';
import { applyWindow, type WindowSpec } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnNameAst, ColumnNameAstSchema, columnNameAstString } from '../../schema/keyword.ts';
import {
  type ColumnType,
  isNumericColumn,
  isTemporalColumn,
  nestedDatasetCol,
  nullableCol,
  type Schema,
  schemaFromEntries,
  unknownCol,
} from '../../schema/types.ts';
import type { Cell, MaterializedDataset } from '../../value.ts';
import { classifyPerCell, columnNameAstName, liftPerCellOnColumns, resolveDsArg } from './helpers.ts';

type WindowKind = WindowSpec['kind'];

/** Inner TS-ref column names — the `{timestamp, value, ...}` shape a
 * nested-dataset cell carries on auto-dispatch to the per-cell path. */
const TS_VALUE_COL = 'value';
const TS_TIME_COL = 'timestamp';

/** Keyword fields shared by every window op. */
const WINDOW_FRAME_FIELDS = {
  'order-by': ColumnNameAstSchema.optional(),
  'partition-by': z.array(ColumnNameAstSchema).optional(),
} as const;
/** Rolling-window size: a fixed-duration string (`"7d"`, `"12h"`) or a row count. */
const WINDOW_SIZE_FIELDS = { window: z.union([z.number().int().positive(), z.string().min(1)]).optional() } as const;

/** Exponential-smoothing decay: one of `:halflife` (duration), `:alpha` (0–1), or `:span` (n). */
const EWMA_FIELDS = {
  halflife: z.string().min(1).optional(),
  alpha: z.coerce.number().gt(0).lte(1).optional(),
  span: z.coerce.number().int().positive().optional(),
} as const;

/** Rate axis: the time unit a per-time op normalises to (`:per "day"`). */
const RATE_FIELDS = { per: z.string().min(1).optional() } as const;

const AS_FIELD = { as: ColumnNameAstSchema.optional() } as const;
const FILL_FIELDS = { limit: z.coerce.number().int().positive().optional() } as const;
const SHIFT_FIELDS = { ...AS_FIELD, offset: z.coerce.number().int().positive().optional() } as const;
const VALUE_FIELDS = { ...AS_FIELD } as const;

/** Raw parsed kwargs a window op's `apply` receives — the superset of every
 * window op's keyword fields. SSOT for the shape threaded through
 * `buildWindowApply` → `evalCommon`. */
type WindowKwargs = {
  readonly params: readonly AstInput[];
  readonly 'order-by'?: ColumnNameAst;
  readonly 'partition-by'?: readonly ColumnNameAst[];
  readonly as?: ColumnNameAst;
  readonly offset?: number;
  readonly limit?: number;
  readonly window?: number | string;
  readonly halflife?: string;
  readonly alpha?: number;
  readonly span?: number;
  readonly per?: string;
};

/** Parsed shape every window op's apply hands to the shared resolvers.
 * `cols` is the resolved string names (variadic for fills, exactly one
 * for the single-col ops). */
type WindowData = {
  readonly cols: readonly string[];
  readonly orderBy: ColumnNameAst | undefined;
  readonly partitionBy: readonly ColumnNameAst[];
  readonly as: ColumnNameAst | undefined;
  readonly offset: number | undefined;
  readonly limit: number | undefined;
  readonly window: number | string | undefined;
  readonly halflife: string | undefined;
  readonly alpha: number | undefined;
  readonly span: number | undefined;
  readonly per: string | undefined;
};

const nullableOf = (t: ColumnType | undefined): ColumnType => nullableCol(t ?? unknownCol);

/** Default output-column suffix per window kind (`<col>_<suffix>`). Fills are
 * absent (they mutate in place); lag/lead are offset-dependent (see
 * {@link defaultOutName}). SSOT for the column-producing ops' naming. */
const OUT_NAME_SUFFIX: Partial<Record<WindowKind, string>> = {
  'first-value': 'first',
  'last-value': 'last',
  'rolling-mean': 'rolling_mean',
  'rolling-sum': 'rolling_sum',
  'rolling-min': 'rolling_min',
  'rolling-max': 'rolling_max',
  'rolling-stddev': 'rolling_stddev',
  'rolling-slope': 'rolling_slope',
  ewma: 'ewma',
  cumsum: 'cumsum',
  cummax: 'cummax',
  cummin: 'cummin',
  'cum-integral': 'integral',
  delta: 'delta',
  'pct-change': 'pct_change',
  'rate-of-change': 'rate',
};

const defaultOutName = (kind: WindowKind, col: string, offset: number): string => {
  // lag/lead fold the offset into the name; fills (no map entry) keep the
  // source column name. Everything else takes a fixed suffix.
  if (kind === 'lag' || kind === 'lead') {
    return `${col}_${kind}${offset}`;
  }
  const suffix = OUT_NAME_SUFFIX[kind];
  return suffix === undefined ? col : `${col}_${suffix}`;
};

const addsColumn = (kind: WindowKind): boolean => kind !== 'ffill' && kind !== 'bfill';

const resolvedOutName = (data: WindowData, kind: WindowKind, col: string): string =>
  data.as === undefined ? defaultOutName(kind, col, data.offset ?? 1) : columnNameAstString(data.as);

/**
 * Resolve `:order-by` / `:partition-by` into a validated frame against
 * `schema`. `inPerCell` switches diagnostics and defaults: per-cell
 * uses `timestamp` as the natural order key and rejects
 * `:partition-by` (each cell is already a single series).
 */
const buildWindowFrame = (
  data: WindowData,
  schema: Schema,
  ctx: SpecialCtx,
  opName: string,
  inPerCell: boolean,
): { readonly order: WindowSpec['order']; readonly partitionBy: readonly string[] } => {
  const pos = opCallPos(ctx.spans, ctx.path);
  if (inPerCell && data.partitionBy.length > 0) {
    throw new ShapeError(
      ':partition-by is not valid on the per-cell (TS-ref) form — each cell is already a single series',
      pos,
      ':partition-by',
    );
  }
  const partitionBy = inPerCell ? [] : data.partitionBy.map(columnNameAstString);
  const missingPartition = partitionBy.find((p) => !schema.has(p));
  if (missingPartition !== undefined) {
    throw new TypeMismatchError(
      `:partition-by column \`${missingPartition}\` to exist on the threaded dataset`,
      'no such column',
      opName,
      pos,
    );
  }
  const resolveOrderColumn = (): string | undefined => {
    if (data.orderBy !== undefined) {
      return columnNameAstString(data.orderBy);
    }
    return schema.has(TS_TIME_COL) ? TS_TIME_COL : undefined;
  };
  const orderColumn = resolveOrderColumn();
  if (orderColumn === undefined) {
    return { order: undefined, partitionBy };
  }
  if (!schema.has(orderColumn)) {
    throw new TypeMismatchError(
      `:order-by column \`${orderColumn}\` to exist on the ${inPerCell ? 'timeseries cell' : 'threaded dataset'}`,
      'no such column',
      opName,
      pos,
    );
  }
  const t = schema.get(orderColumn);
  return { order: { column: orderColumn, dateAware: t !== undefined && isTemporalColumn(t) }, partitionBy };
};

/** Inner schema-after for the per-cell form: append a nullable derived
 * column for the column-producing ops, pass through for fills. The
 * derived column's name follows `resolvedOutName(<outerCol>, …)` so
 * outer naming is consistent; its type is `nullable(<targetCol>'s
 * type)` where `targetCol` is the resolved inner data column (`:value`
 * for raw TS-ref or the lone numeric col for post-bucket cells). */
const innerSchemaAfter = (
  innerSchema: Schema,
  kind: WindowKind,
  data: WindowData,
  outerCol: string,
  targetCol: string,
): Schema => {
  if (!addsColumn(kind)) {
    return innerSchema;
  }
  const out = resolvedOutName(data, kind, outerCol);
  return schemaFromEntries([...innerSchema, [out, nullableOf(innerSchema.get(targetCol))]]);
};

/**
 * Resolve the inner data column a window op should operate on for a
 * single per-cell spec. Raw TS-ref cells carry `:value` → return that
 * verbatim. Already-reduced cells whose inner schema lacks `:value`
 * but exposes exactly one numeric column (per {@link isNumericColumn})
 * pick that single numeric column. Otherwise `undefined` — the caller
 * surfaces a precise error.
 *
 * SSOT for "what's the target inner column" — keeps `applyPerCell` and
 * {@link buildWindowPropagateSchema} from drifting.
 */
const resolveInnerTargetCol = (innerSchema: Schema): string | undefined => {
  if (innerSchema.has(TS_VALUE_COL)) {
    return TS_VALUE_COL;
  }
  const numeric = Array.from(innerSchema.entries())
    .filter(([, t]) => isNumericColumn(t))
    .map(([k]) => k);
  return numeric.length === 1 ? numeric[0] : undefined;
};

/**
 * SSOT for the translated plain-form window-op plan node recorded on
 * each lazy per-cell. Takes the resolved cell-local target column and
 * the outer kwargs (order-by / partition-by / offset / limit), emits
 * the standard plain-form call shape with `:as` set to the outer-
 * derived name so naming round-trips through the replay.
 *
 * Symmetrical with `applyPerCell`'s eager branch — both produce
 * identical results given the same target col and frame kwargs.
 */
const buildWindowCellPlanNode = (args: {
  readonly opName: string;
  readonly kind: WindowKind;
  readonly outerCol: string;
  readonly targetCol: string;
  readonly data: WindowData;
  readonly schemaAfter: Schema;
}): PlanNode => {
  const { opName, kind, outerCol, targetCol, data, schemaAfter } = args;
  const targetKw = { $kw: targetCol } as const;
  const orderByKw = data.orderBy === undefined ? undefined : data.orderBy;
  const partitionByKws = data.partitionBy.length === 0 ? undefined : data.partitionBy;
  // Scalar passthrough kwargs round-trip verbatim — emit only the set ones.
  const scalarKwargs = {
    offset: data.offset,
    limit: data.limit,
    window: data.window,
    halflife: data.halflife,
    alpha: data.alpha,
    span: data.span,
    per: data.per,
  };
  const callArgs: OpCall = {
    // `opName` is the window op's own registered head, threaded as a string
    // through the per-cell builder; it is always a real opcode by construction.
    name: opName as Opcode,
    params: [targetKw],
    ...(orderByKw === undefined ? {} : { 'order-by': orderByKw }),
    ...(partitionByKws === undefined ? {} : { 'partition-by': partitionByKws }),
    ...(addsColumn(kind) ? { as: { $kw: resolvedOutName(data, kind, outerCol) } } : {}),
    ...Object.fromEntries(Object.entries(scalarKwargs).filter(([, v]) => v !== undefined)),
  };
  return { op: opName, args: callArgs, schemaAfter };
};

/** Outer schema-after for the plain form: fills are identity;
 * column-producing ops append a nullable derived column. */
const plainSchemaAfter = (schema: Schema, kind: WindowKind, data: WindowData, col: string): Schema => {
  if (!addsColumn(kind)) {
    return schema;
  }
  const out = resolvedOutName(data, kind, col);
  return schemaFromEntries([...schema, [out, nullableOf(schema.get(col))]]);
};

/** Fills' target columns on the plain form: named varargs validated
 * against the schema, else every column except the frame keys. */
const resolveFillTargets = (
  cols: readonly string[],
  schema: Schema,
  frameKeys: ReadonlySet<string>,
  ctx: SpecialCtx,
  opName: string,
): readonly string[] => {
  if (cols.length > 0) {
    const missing = cols.find((c) => !schema.has(c));
    if (missing !== undefined) {
      throw new TypeMismatchError(
        `column \`${missing}\` to exist on the threaded dataset`,
        'no such column',
        opName,
        opCallPos(ctx.spans, ctx.path),
      );
    }
    return cols;
  }
  return Array.from(schema.keys()).filter((c) => !frameKeys.has(c));
};

/** Build a `WindowSpec` for a column-producing op (lag/lead/first/last)
 * on a target column with a pre-resolved frame. */
const buildShiftOrValueSpec = (
  kind: 'lag' | 'lead' | 'first-value' | 'last-value',
  col: string,
  outCol: string,
  frame: { readonly order: WindowSpec['order']; readonly partitionBy: readonly string[] },
  data: WindowData,
): WindowSpec => {
  if (kind === 'first-value' || kind === 'last-value') {
    return { kind, col, out: outCol, order: frame.order, partitionBy: frame.partitionBy };
  }
  return {
    kind,
    col,
    out: outCol,
    offset: data.offset ?? 1,
    default: null as Cell,
    order: frame.order,
    partitionBy: frame.partitionBy,
  };
};
/** Column-producing "scan" ops (rolling / cumulative / rate) — computed via
 * JS kernels rather than arquero's native window machinery. */
type ScanKind = Exclude<WindowKind, 'ffill' | 'bfill' | 'first-value' | 'last-value' | 'lag' | 'lead'>;

const isScanKind = (kind: WindowKind): kind is ScanKind =>
  kind !== 'ffill' &&
  kind !== 'bfill' &&
  kind !== 'first-value' &&
  kind !== 'last-value' &&
  kind !== 'lag' &&
  kind !== 'lead';

/** Resolve `:window` into a `WindowExtent` — a row count (`20`) or a duration
 * span (`"7d"`). A duration span needs a frame order to measure against. */
const resolveWindowExtent = (
  data: WindowData,
  frame: { readonly order: WindowSpec['order'] },
  pos: SourcePos,
  opName: string,
): WindowExtent => {
  const w = data.window;
  if (w === undefined) {
    throw new ShapeError(`${opName} requires :window — a duration ("7d", "12h") or a row count (20)`, pos, ':window');
  }
  if (typeof w === 'number') {
    return { unit: 'rows', count: w };
  }
  const ms = parseDurationMs(w);
  if (ms === null) {
    throw new ShapeError(`${opName} :window "${w}" is not a valid duration (e.g. "7d", "12h", "30m")`, pos, ':window');
  }
  if (frame.order === undefined) {
    throw new ShapeError(
      `${opName} :window "${w}" is a time span and needs an :order-by time column`,
      pos,
      ':order-by',
    );
  }
  return { unit: 'duration', ms };
};

/** Resolve `:per <unit>` (default `day`) to milliseconds — the rate/integral axis. */
const resolvePerMs = (data: WindowData, pos: SourcePos, opName: string): number => {
  const unit = data.per ?? 'day';
  const ms = parseTimeUnitMs(unit);
  if (ms === null) {
    throw new ShapeError(`${opName} :per "${unit}" is not a time unit (second|minute|hour|day|week)`, pos, ':per');
  }
  return ms;
};

/** Resolve EWMA decay from exactly one of `:halflife`, `:alpha`, or `:span`. */
const resolveEwmaDecay = (data: WindowData, pos: SourcePos, opName: string): EwmaDecay => {
  if (data.halflife !== undefined) {
    const ms = parseDurationMs(data.halflife);
    if (ms === null) {
      throw new ShapeError(`${opName} :halflife "${data.halflife}" is not a valid duration`, pos, ':halflife');
    }
    return { kind: 'halflife', ms };
  }
  if (data.alpha !== undefined) {
    return { kind: 'alpha', alpha: data.alpha };
  }
  if (data.span !== undefined) {
    return { kind: 'alpha', alpha: 2 / (data.span + 1) };
  }
  throw new ShapeError(`${opName} requires one of :halflife <dur>, :alpha <0-1>, or :span <n>`, pos, ':halflife');
};

/** Inputs each scan-spec builder needs. `base` carries the frame-independent
 * `col`/`out` plus the resolved order/partition. */
type ScanSpecArgs = {
  readonly base: {
    readonly col: string;
    readonly out: string;
    readonly order: WindowSpec['order'];
    readonly partitionBy: readonly string[];
  };
  readonly frame: { readonly order: WindowSpec['order']; readonly partitionBy: readonly string[] };
  readonly data: WindowData;
  readonly pos: SourcePos;
  readonly opName: string;
};

/** Per-scan-kind `WindowSpec` builders. The mapped type keeps it exhaustive and
 * lets each entry return its precisely-narrowed arm; the op-specific kwargs
 * (`:window`, decay, `:per`) are resolved here. */
const SCAN_SPEC_BUILDERS: {
  readonly [KindType in ScanKind]: (a: ScanSpecArgs) => Extract<WindowSpec, { kind: KindType }>;
} = {
  'rolling-mean': ({ base, frame, data, pos, opName }) => ({
    kind: 'rolling-mean',
    ...base,
    window: resolveWindowExtent(data, frame, pos, opName),
  }),
  'rolling-sum': ({ base, frame, data, pos, opName }) => ({
    kind: 'rolling-sum',
    ...base,
    window: resolveWindowExtent(data, frame, pos, opName),
  }),
  'rolling-min': ({ base, frame, data, pos, opName }) => ({
    kind: 'rolling-min',
    ...base,
    window: resolveWindowExtent(data, frame, pos, opName),
  }),
  'rolling-max': ({ base, frame, data, pos, opName }) => ({
    kind: 'rolling-max',
    ...base,
    window: resolveWindowExtent(data, frame, pos, opName),
  }),
  'rolling-stddev': ({ base, frame, data, pos, opName }) => ({
    kind: 'rolling-stddev',
    ...base,
    window: resolveWindowExtent(data, frame, pos, opName),
  }),
  'rolling-slope': ({ base, frame, data, pos, opName }) => ({
    kind: 'rolling-slope',
    ...base,
    window: resolveWindowExtent(data, frame, pos, opName),
    // Per-time slope only makes sense on a temporal axis; otherwise raw value-per-x.
    scale: frame.order?.dateAware ? resolvePerMs(data, pos, opName) : 1,
  }),
  ewma: ({ base, data, pos, opName }) => ({ kind: 'ewma', ...base, decay: resolveEwmaDecay(data, pos, opName) }),
  cumsum: ({ base }) => ({ kind: 'cumsum', ...base }),
  cummax: ({ base }) => ({ kind: 'cummax', ...base }),
  cummin: ({ base }) => ({ kind: 'cummin', ...base }),
  'cum-integral': ({ base, data, pos, opName }) => ({
    kind: 'cum-integral',
    ...base,
    scale: resolvePerMs(data, pos, opName),
  }),
  delta: ({ base }) => ({ kind: 'delta', ...base }),
  'pct-change': ({ base }) => ({ kind: 'pct-change', ...base }),
  'rate-of-change': ({ base, data, pos, opName }) => ({
    kind: 'rate-of-change',
    ...base,
    scale: resolvePerMs(data, pos, opName),
  }),
};
/**
 * Op names that are valid only as pipeline stages (column-producing scans) and
 * never as aggregate reducers. Derived from {@link SCAN_SPEC_BUILDERS} so it
 * cannot drift from the registered scan ops; consulted by the agg-scope error
 * to explain the transform-then-reduce remedy.
 */
export const SCAN_OP_NAMES: ReadonlySet<string> = new Set(Object.keys(SCAN_SPEC_BUILDERS));

/** Build a `WindowSpec` for a column-producing scan op on a resolved frame. */
const buildScanSpec = (
  kind: ScanKind,
  col: string,
  outCol: string,
  frame: { readonly order: WindowSpec['order']; readonly partitionBy: readonly string[] },
  data: WindowData,
  pos: SourcePos,
  opName: string,
): WindowSpec => {
  const base = { col, out: outCol, order: frame.order, partitionBy: frame.partitionBy };
  return (SCAN_SPEC_BUILDERS[kind] as (a: ScanSpecArgs) => WindowSpec)({ base, frame, data, pos, opName });
};

/** Plain (outer-dataset) path. Fills are variadic and schema-preserving;
 * single-column ops derive one new column. */
const applyPlain = (
  ds: MaterializedDataset,
  data: WindowData,
  ctx: SpecialCtx,
  kind: WindowKind,
  opName: string,
): MaterializedDataset => {
  if (kind === 'ffill' || kind === 'bfill') {
    const frame = buildWindowFrame(data, ds.schema, ctx, opName, false);
    const frameKeys = new Set<string>([...(frame.order ? [frame.order.column] : []), ...frame.partitionBy]);
    const targets = resolveFillTargets(data.cols, ds.schema, frameKeys, ctx, opName);
    return targets.reduce(
      (acc, col) =>
        applyWindow(
          acc,
          { kind, col, order: frame.order, partitionBy: frame.partitionBy, limit: data.limit },
          acc.schema,
        ),
      ds,
    );
  }
  const col = data.cols[0];
  if (col === undefined || !ds.schema.has(col)) {
    throw new TypeMismatchError(
      `column \`${col ?? ''}\` to exist on the threaded dataset`,
      'no such column',
      opName,
      opCallPos(ctx.spans, ctx.path),
    );
  }
  const frame = buildWindowFrame(data, ds.schema, ctx, opName, false);
  const out = resolvedOutName(data, kind, col);
  const spec = isScanKind(kind)
    ? buildScanSpec(kind, col, out, frame, data, opCallPos(ctx.spans, ctx.path), opName)
    : buildShiftOrValueSpec(kind, col, out, frame, data);
  return applyWindow(ds, spec, plainSchemaAfter(ds.schema, kind, data, col));
};

/** Per-cell (TS-ref) path. Each listed column is `nested-dataset`;
 * walk each row and apply the window kernel to the inner cell. Target
 * column is resolved per-cell via {@link resolveInnerTargetCol} so the
 * same path handles raw TS-ref cells (`:value`) and already-reduced
 * cells (the lone numeric inner col) uniformly. Ambiguous inner
 * schemas (multiple numeric cols) are unambiguously rejected with a
 * precise error.
 *
 * Lazy cells defer: a translated plain-form window-op plan node is
 * appended to the cell's plan (`(<op> cell <targetCol> :order-by …
 * :partition-by … :as <outer>_<kind> :offset N :limit N)`), preserving
 * the substrate's "lazy cells stay lazy through per-cell lift"
 * invariant. Materialised cells run the kernel eagerly. */
const applyPerCell = (
  ds: MaterializedDataset,
  data: WindowData,
  ctx: SpecialCtx,
  kind: WindowKind,
  opName: string,
): Promise<MaterializedDataset> => {
  const callPos = opCallPos(ctx.spans, ctx.path);
  const specs = data.cols.map((col) => {
    const colType = ds.schema.get(col);
    if (colType?.kind !== 'nested-dataset') {
      throw new TypeMismatchError(`column \`${col}\` to be nested-dataset`, 'not nested-dataset', opName, callPos);
    }
    const target = resolveInnerTargetCol(colType.innerSchema);
    if (target === undefined) {
      throw new ShapeError(
        `${opName} on \`${col}\`: cannot resolve a target inner column. Expected inner \`:value\` (raw TS-ref shape) or exactly one numeric inner column; got {${Array.from(colType.innerSchema.keys()).join(', ')}}. Unnest the column first, or reduce the inner schema to a single numeric column upstream.`,
        callPos,
      );
    }
    const innerAfter = innerSchemaAfter(colType.innerSchema, kind, data, col, target);
    return {
      col,
      innerSchemaAfter: innerAfter,
      cellPlanNodes: (_innerSchemaBefore: Schema): readonly PlanNode[] => [
        buildWindowCellPlanNode({ opName, kind, outerCol: col, targetCol: target, data, schemaAfter: innerAfter }),
      ],
      cellFn: async (innerDs: MaterializedDataset): Promise<MaterializedDataset> => {
        const frame = buildWindowFrame(data, innerDs.schema, ctx, opName, true);
        if (kind === 'ffill' || kind === 'bfill') {
          return applyWindow(
            innerDs,
            { kind, col: target, order: frame.order, partitionBy: frame.partitionBy, limit: data.limit },
            innerDs.schema,
          );
        }
        const out = resolvedOutName(data, kind, col);
        const spec = isScanKind(kind)
          ? buildScanSpec(kind, target, out, frame, data, callPos, opName)
          : buildShiftOrValueSpec(kind, target, out, frame, data);
        return applyWindow(innerDs, spec, innerAfter);
      },
    };
  });
  return liftPerCellOnColumns(ds, specs, ctx);
};

/** Auto-dispatch: any listed col with kind `nested-dataset` => per-cell;
 * else plain. Mixing kinds in one call is a `ShapeError`. */
const windowDispatch = async (
  ds: MaterializedDataset,
  data: WindowData,
  ctx: SpecialCtx,
  kind: WindowKind,
  opName: string,
): Promise<MaterializedDataset> => {
  const isPerCell = classifyPerCell(data.cols, ds.schema, opName, 'plain', opCallPos(ctx.spans, ctx.path));
  return isPerCell ? applyPerCell(ds, data, ctx, kind, opName) : applyPlain(ds, data, ctx, kind, opName);
};

/** Common evaluation prelude: evaluate the dataset positional, resolve
 * it to a `MaterializedDataset`, normalize the parsed kwargs into a
 * `WindowData`. The dataset is `params[0]`; cols are `params[1..]`. */
const evalCommon = async (
  data: WindowKwargs,
  ctx: SpecialCtx,
  opLabel: string,
): Promise<{ readonly ds: MaterializedDataset; readonly windowData: WindowData }> => {
  const [dsAst, ...colAsts] = data.params;
  const { value: dsValue } = await ctx.evalNode(dsAst as AstInput, ctx.env, paramPath(ctx.path, 0));
  const ds = await resolveDsArg(dsValue, opLabel, ctx);
  const cols = (colAsts as readonly ColumnNameAst[]).map(columnNameAstString);
  return {
    ds,
    windowData: {
      cols,
      orderBy: data['order-by'],
      partitionBy: data['partition-by'] ?? [],
      as: data.as,
      offset: data.offset,
      limit: data.limit,
      window: data.window,
      halflife: data.halflife,
      alpha: data.alpha,
      span: data.span,
      per: data.per,
    },
  };
};

/** Factory for each op's `apply` — the boilerplate is identical save
 * for `kind` and `opName`. SSOT for the special-eval dispatch wiring. */
const buildWindowApply =
  (kind: WindowKind, opName: string) =>
  async (data: WindowKwargs, ctx: SpecialCtx): Promise<EvalResult> => {
    const { ds, windowData } = await evalCommon(data, ctx, `(${opName} ds :col ...)`);
    return { value: await windowDispatch(ds, windowData, ctx, kind, opName), env: ctx.env };
  };
/**
 * Build `propagateSchema` for a window op. Resolves the form
 * (plain vs per-cell) from the input schema, then either passes
 * through (fills) or appends a derived col per listed col (shift /
 * value ops). Per-cell form rewrites each lifted col to
 * `nested-dataset(innerSchemaAfter)`; plain form folds `plainSchemaAfter`
 * over the cols sequentially.
 */
const buildWindowPropagateSchema =
  (kind: WindowKind) =>
  (data: unknown, ctx: PropagateSchemaCtx): Schema | undefined => {
    const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
    const colAsts = params.slice(1);
    const cols = colAsts.flatMap((c) => {
      const nm = columnNameAstName(c);
      return nm === undefined ? [] : [nm];
    });
    const kwargs = data as WindowKwargs;
    const windowData: WindowData = {
      cols,
      orderBy: kwargs['order-by'],
      partitionBy: kwargs['partition-by'] ?? [],
      as: kwargs.as,
      offset: kwargs.offset,
      limit: kwargs.limit,
      window: kwargs.window,
      halflife: kwargs.halflife,
      alpha: kwargs.alpha,
      span: kwargs.span,
      per: kwargs.per,
    };
    const isPerCell = cols.some((c) => ctx.inputSchema.get(c)?.kind === 'nested-dataset');
    if (isPerCell) {
      // Rewrite each lifted col to `nested-dataset(innerSchemaAfter)`;
      // non-lifted cols pass through. Shift / value kinds append a
      // nullable derived col to the inner schema; fills are identity.
      // An ambiguous inner schema (no `:value` and >1 numeric col) makes
      // the propagator return `undefined` → runtime-width path → eager
      // re-dispatch surfaces the precise error from `applyPerCell`.
      const liftSet = new Set(cols);
      const entries = Array.from(ctx.inputSchema.entries()).map(([k, t]) => {
        if (!liftSet.has(k) || t.kind !== 'nested-dataset') {
          return [k, t] as const;
        }
        const target = resolveInnerTargetCol(t.innerSchema);
        if (target === undefined) {
          return undefined;
        }
        return [k, nestedDatasetCol(innerSchemaAfter(t.innerSchema, kind, windowData, k, target))] as const;
      });
      if (entries.some((e) => e === undefined)) {
        return undefined;
      }
      return schemaFromEntries(entries as readonly (readonly [string, ColumnType])[]);
    }
    // Plain form. Fills are variadic; an empty cols list defaults to
    // "every column except the frame keys" (mirrors `resolveFillTargets`).
    // For column-producing kinds (lag/lead/first/last) the Zod tuple
    // already enforces exactly one col.
    const effectiveCols = ((): readonly string[] => {
      if (cols.length > 0 || addsColumn(kind)) {
        return cols;
      }
      const frameKeys = new Set<string>(
        [
          windowData.orderBy === undefined ? undefined : columnNameAstString(windowData.orderBy),
          ...windowData.partitionBy.map(columnNameAstString),
        ].filter((k): k is string => k !== undefined),
      );
      return Array.from(ctx.inputSchema.keys()).filter((c) => !frameKeys.has(c));
    })();
    return effectiveCols.reduce<Schema>((acc, col) => plainSchemaAfter(acc, kind, windowData, col), ctx.inputSchema);
  };

export const forwardFill = z
  .object({
    name: z.literal('forward-fill'),
    params: z.tuple([astArg('<dataset>')]).rest(ColumnNameAstSchema),
    ...WINDOW_FRAME_FIELDS,
    ...FILL_FIELDS,
  })
  .describe('Forward-fill (LOCF): each null is replaced by the nearest preceding non-null within its frame.')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> readings (forward-fill :level :order-by :ts :partition-by [:tank]))' },
    terseGroup: 'fill',
    apply: buildWindowApply('ffill', 'forward-fill'),
    dataset: { propagateSchema: buildWindowPropagateSchema('ffill') },
  });

export const backwardFill = z
  .object({
    name: z.literal('backward-fill'),
    params: z.tuple([astArg('<dataset>')]).rest(ColumnNameAstSchema),
    ...WINDOW_FRAME_FIELDS,
    ...FILL_FIELDS,
  })
  .describe('Backward-fill (NOCB): each null is replaced by the nearest following non-null within its frame.')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> readings (backward-fill :level :order-by :ts :partition-by [:tank]))' },
    terseGroup: 'fill',
    apply: buildWindowApply('bfill', 'backward-fill'),
    dataset: { propagateSchema: buildWindowPropagateSchema('bfill') },
  });

export const lag = z
  .object({
    name: z.literal('lag'),
    params: z.tuple([astArg('<dataset>'), ColumnNameAstSchema]),
    ...WINDOW_FRAME_FIELDS,
    ...SHIFT_FIELDS,
  })
  .describe('Add a column with the value `:offset` rows earlier within the frame (SQL `LAG`).')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> trades (lag :price :order-by :ts :partition-by [:symbol] :offset 1))' },
    terseGroup: 'shift',
    apply: buildWindowApply('lag', 'lag'),
    dataset: { propagateSchema: buildWindowPropagateSchema('lag') },
  });

export const lead = z
  .object({
    name: z.literal('lead'),
    params: z.tuple([astArg('<dataset>'), ColumnNameAstSchema]),
    ...WINDOW_FRAME_FIELDS,
    ...SHIFT_FIELDS,
  })
  .describe('Add a column with the value `:offset` rows later within the frame (SQL `LEAD`).')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> trades (lead :price :order-by :ts :partition-by [:symbol]))' },
    terseGroup: 'shift',
    apply: buildWindowApply('lead', 'lead'),
    dataset: { propagateSchema: buildWindowPropagateSchema('lead') },
  });

export const firstValue = z
  .object({
    name: z.literal('first-value'),
    params: z.tuple([astArg('<dataset>'), ColumnNameAstSchema]),
    ...WINDOW_FRAME_FIELDS,
    ...VALUE_FIELDS,
  })
  .describe('Add a column with the first non-null value of the column within each frame, broadcast to every row.')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> readings (first-value :level :order-by :ts :partition-by [:tank]))' },
    terseGroup: 'pick',
    apply: buildWindowApply('first-value', 'first-value'),
    dataset: { propagateSchema: buildWindowPropagateSchema('first-value') },
  });

export const lastValue = z
  .object({
    name: z.literal('last-value'),
    params: z.tuple([astArg('<dataset>'), ColumnNameAstSchema]),
    ...WINDOW_FRAME_FIELDS,
    ...VALUE_FIELDS,
  })
  .describe('Add a column with the last non-null value of the column within each frame, broadcast to every row.')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> readings (last-value :level :order-by :ts :partition-by [:tank]))' },
    terseGroup: 'pick',
    apply: buildWindowApply('last-value', 'last-value'),
    dataset: { propagateSchema: buildWindowPropagateSchema('last-value') },
  });
/**
 * Factory for a column-producing scan op (rolling / cumulative / rate). Every
 * one takes `(<dataset> <col>)` plus the shared frame (`:order-by`,
 * `:partition-by`) and `:as` kwargs; `extraFields` adds the op-specific ones
 * (`:window`, `:halflife`/`:alpha`/`:span`, `:per`). SSOT for the scan-op
 * registration boilerplate — `name` doubles as the `WindowSpec` kind.
 */
const makeScanOp = <NameType extends ScanKind>(
  name: NameType,
  extraFields: z.ZodRawShape,
  description: string,
  example: string,
  terseGroup?: TerseGroupId,
) =>
  z
    .object({
      name: z.literal(name),
      params: z.tuple([astArg('<dataset>'), ColumnNameAstSchema]),
      ...WINDOW_FRAME_FIELDS,
      ...AS_FIELD,
      ...extraFields,
    })
    .describe(description)
    .register(opMeta, {
      evaluation: 'special',
      effect: 'pure',
      example: { expr: example },
      terseGroup,
      apply: buildWindowApply(name, name),
      dataset: { propagateSchema: buildWindowPropagateSchema(name) },
    });

export const rollingMean = makeScanOp(
  'rolling-mean',
  WINDOW_SIZE_FIELDS,
  'Rolling (moving) average of a column over a trailing `:window`.',
  '(-> well-rates (rolling-mean :rate :window "7d"))',
  'rolling',
);

export const rollingSum = makeScanOp(
  'rolling-sum',
  WINDOW_SIZE_FIELDS,
  'Rolling sum of a column over a trailing `:window`.',
  '(-> production (rolling-sum :oil :window "30d"))',
  'rolling',
);

export const rollingMin = makeScanOp(
  'rolling-min',
  WINDOW_SIZE_FIELDS,
  'Rolling minimum of a column over a trailing `:window`.',
  '(-> sensor (rolling-min :pressure :window "1d"))',
  'rolling',
);

export const rollingMax = makeScanOp(
  'rolling-max',
  WINDOW_SIZE_FIELDS,
  'Rolling maximum of a column over a trailing `:window`.',
  '(-> sensor (rolling-max :pressure :window "1d"))',
  'rolling',
);

export const rollingStddev = makeScanOp(
  'rolling-stddev',
  WINDOW_SIZE_FIELDS,
  'Rolling standard deviation (volatility) of a column over a trailing `:window` — population stddev.',
  '(-> sensor (rolling-stddev :pressure :window "1d"))',
  'rolling',
);

export const rollingSlope = makeScanOp(
  'rolling-slope',
  { ...WINDOW_SIZE_FIELDS, ...RATE_FIELDS },
  'Rolling least-squares trend slope of a column over a trailing `:window`. On a time axis the slope is expressed per `:per` unit (default `day`), so a rising/falling trend reads in value-per-day.',
  '(-> well-rates (rolling-slope :rate :window "30d" :per "day"))',
);

export const ewma = makeScanOp(
  'ewma',
  EWMA_FIELDS,
  'Exponentially-weighted moving average. Set the decay with exactly one of `:halflife` (a time span like `"3d"` — adapts to irregular sampling), `:alpha` (0–1), or `:span` (n). Smooths noisy sensor data while staying responsive.',
  '(-> sensor (ewma :temperature :halflife "6h"))',
);

export const cumsum = makeScanOp(
  'cumsum',
  {},
  'Cumulative (running) sum of a column within its frame.',
  '(-> daily (cumsum :produced-volume))',
  'cum',
);

export const cumIntegral = makeScanOp(
  'cum-integral',
  RATE_FIELDS,
  'Time-weighted cumulative integral (trapezoidal) of a flow rate → cumulative volume. `:per` is the rate’s time unit (default `day`, i.e. a per-day rate), so `∫ rate·dt` comes out in the rate’s base unit (e.g. bbl from bbl/day). This is the physically-correct cumulative production from a rate.',
  '(-> well-rates (cum-integral :oil-rate :per "day"))',
);

export const cummax = makeScanOp(
  'cummax',
  {},
  'Running maximum (peak-to-date) of a column within its frame.',
  '(-> well-rates (cummax :rate))',
  'cum',
);

export const cummin = makeScanOp(
  'cummin',
  {},
  'Running minimum (trough-to-date) of a column within its frame.',
  '(-> well-rates (cummin :rate))',
  'cum',
);

export const delta = makeScanOp(
  'delta',
  {},
  'First difference: a column’s value minus the previous row’s value within the frame.',
  '(-> daily (delta :reading))',
  'diff',
);

export const pctChange = makeScanOp(
  'pct-change',
  {},
  'Fractional change from the previous row: (v − vₚᵣₑᵥ) / vₚᵣₑᵥ.',
  '(-> daily (pct-change :rate))',
  'diff',
);

export const rateOfChange = makeScanOp(
  'rate-of-change',
  RATE_FIELDS,
  'Discrete rate of change Δv/Δt against the previous row, normalised to `:per`.',
  '(-> well-rates (rate-of-change :pressure :per "hour"))',
  'diff',
);
